import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { IdealCustomerProfile } from '../../../types';
import { apiClient } from '../../../lib/api';
import { useAppData } from '../../../contexts/AppDataContext';

interface UseSidebarStateProps {
  defaultCollapsed?: boolean;
}

export const useSidebarState = ({ defaultCollapsed = false }: UseSidebarStateProps) => {
  const location = useLocation();
  const sidebarRef = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);

  // Get campaigns from context (cached, doesn't refetch on navigation)
  const { campaigns: sentCampaigns, isLoadingCampaigns, hasMoreCampaigns, loadMoreCampaigns } = useAppData();

  const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed);
  const [showOverlay, setShowOverlay] = useState(false);
  const [overlayContent, setOverlayContent] = useState<'datasets' | null>(null);
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const [searchQuery] = useState('');
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);

  const [icpProfiles, setIcpProfiles] = useState<IdealCustomerProfile[]>([]);
  const [expandedIcpIds, setExpandedIcpIds] = useState<Set<string>>(new Set());
  const [editingIcp, setEditingIcp] = useState<IdealCustomerProfile | null>(null);
  const [isSavingIcp, setIsSavingIcp] = useState(false);

  const [isICPSectionExpanded, setIsICPSectionExpanded] = useState(false);
  const [isDatasetsSectionExpanded, setIsDatasetsSectionExpanded] = useState(true);
  const [isEmailSectionExpanded, setIsEmailSectionExpanded] = useState(false);

  const isOnHome = location.pathname === '/' || location.pathname === '/datasets';

  const toggleSection = useCallback((section: 'icp' | 'datasets' | 'email') => {
    if (section === 'icp') {
      const newState = !isICPSectionExpanded;
      setIsICPSectionExpanded(newState);
      if (newState) {
        setIsDatasetsSectionExpanded(false);
        setIsEmailSectionExpanded(false);
      }
    } else if (section === 'datasets') {
      const newState = !isDatasetsSectionExpanded;
      setIsDatasetsSectionExpanded(newState);
      if (newState) {
        setIsICPSectionExpanded(false);
        setIsEmailSectionExpanded(false);
      }
    } else if (section === 'email') {
      const newState = !isEmailSectionExpanded;
      setIsEmailSectionExpanded(newState);
      if (newState) {
        setIsICPSectionExpanded(false);
        setIsDatasetsSectionExpanded(false);
      }
    }
  }, [isICPSectionExpanded, isDatasetsSectionExpanded, isEmailSectionExpanded]);

  const toggleOverlay = useCallback((content: 'datasets') => {
    if (showOverlay && overlayContent === content) {
      setShowOverlay(false);
      setOverlayContent(null);
    } else {
      setShowOverlay(true);
      setOverlayContent(content);
    }
  }, [showOverlay, overlayContent]);

  const closeOverlay = useCallback(() => {
    setShowOverlay(false);
    setOverlayContent(null);
  }, []);

  const handleMouseEnter = useCallback((e: React.MouseEvent, itemId: string) => {
    if (!isCollapsed) return;
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltipPosition({
      top: rect.top + rect.height / 2,
      left: rect.right + 8,
    });
    setHoveredItem(itemId);
  }, [isCollapsed]);

  const handleMouseLeave = useCallback(() => {
    setHoveredItem(null);
  }, []);

  const toggleIcpExpanded = useCallback((icpId: string) => {
    setExpandedIcpIds(prev => {
      const next = new Set(prev);
      if (next.has(icpId)) {
        next.delete(icpId);
      } else {
        next.add(icpId);
      }
      return next;
    });
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'b') {
        e.preventDefault();
        setIsCollapsed(prev => !prev);
        setShowOverlay(false);
      }
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsSearchModalOpen(true);
      }
      if (e.key === 'Escape' && showOverlay) {
        closeOverlay();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showOverlay, closeOverlay]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        showOverlay &&
        overlayRef.current &&
        !overlayRef.current.contains(e.target as Node) &&
        sidebarRef.current &&
        !sidebarRef.current.contains(e.target as Node)
      ) {
        closeOverlay();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showOverlay, closeOverlay]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setIsCollapsed(true);
        setShowOverlay(false);
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const refreshICPProfiles = useCallback(async () => {
    try {
      const profiles = await apiClient.getICPProfiles();
      setIcpProfiles(profiles || []);
    } catch (error) {
      console.error('Failed to fetch ICP profiles:', error);
    }
  }, []);

  useEffect(() => {
    refreshICPProfiles();
  }, [refreshICPProfiles]);


  return {
    refs: { sidebarRef, overlayRef },
    state: {
      isCollapsed,
      showOverlay,
      overlayContent,
      hoveredItem,
      tooltipPosition,
      searchQuery,
      isSearchModalOpen,
      icpProfiles,
      expandedIcpIds,
      editingIcp,
      isSavingIcp,
      isICPSectionExpanded,
      isDatasetsSectionExpanded,
      isEmailSectionExpanded,
      sentCampaigns,
      isLoadingCampaigns,
      hasMoreCampaigns,
      isOnHome,
    },
    actions: {
      setIsCollapsed,
      setIsSearchModalOpen,
      setEditingIcp,
      setIsSavingIcp,
      setIcpProfiles,
      refreshICPProfiles,
      loadMoreCampaigns,
      toggleSection,
      toggleOverlay,
      closeOverlay,
      handleMouseEnter,
      handleMouseLeave,
      toggleIcpExpanded,
    },
  };
};
